/** Equipment (Demo Mode only) - top-level Estate Management entry point centred on the
 * equipment catalogue: the reusable equipment types/models that could exist across the
 * estate (what equipment is, what it's used for, how it should be maintained). This is the
 * cross-site "library" view - drilling into a row opens the nested Equipment Detail screen
 * (see EquipmentDetail.tsx), which also shows which sites are currently leveraging that
 * catalogue item (the former standalone Site Equipment page's cross-site view). No backend
 * catalogue exists yet - all data is static mock data, matching the same "seeded illustrative
 * numbers" approach used by src/pages/EstateManagement. */

import { useState } from 'react';
import { Box, Chip, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { Inventory, Kitchen, AcUnit, LocalCafe } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

export const MOCK_CATALOGUE = [
  { name: 'Combi Oven', category: 'Cooking', manufacturer: 'Rational', model: 'iCombi Pro 10-1/1', status: 'Approved', processCapabilities: 'Bake, Steam, Roast, Regenerate' },
  { name: 'Fryer, Twin Basket', category: 'Cooking', manufacturer: 'Frymaster', model: 'MJ45', status: 'Approved', processCapabilities: 'Fry, Hold Hot' },
  { name: 'Walk-in Chiller', category: 'Refrigeration', manufacturer: 'Foster', model: 'WI-CH-1.2', status: 'Approved', processCapabilities: 'Chill' },
  { name: 'Blast Freezer', category: 'Freezing', manufacturer: 'Williams', model: 'HydroChill BF', status: 'Approved', processCapabilities: 'Freeze' },
  { name: 'Conveyor Dishwasher', category: 'Warewashing', manufacturer: 'Winterhalter', model: 'PT-M', status: 'Approved', processCapabilities: 'Wash' },
  { name: 'Espresso Machine', category: 'Beverage', manufacturer: 'La Marzocco', model: 'Linea PB', status: 'Approved', processCapabilities: 'Brew, Dispense' },
  { name: 'Hot Cupboard', category: 'Holding', manufacturer: 'Victor', model: 'HC200', status: 'Approved', processCapabilities: 'Hold Hot' },
  { name: 'Deep Fat Fryer (legacy)', category: 'Cooking', manufacturer: 'Various', model: 'N/A', status: 'Deprecated', processCapabilities: 'Fry' },
  { name: 'Smart Fridge/Freezer Sensor', category: 'IoT Kitchen Devices', manufacturer: 'Checkit', model: 'SmartSensor Pro', status: 'Approved', processCapabilities: 'Temperature Monitoring, Alerting' },
  { name: 'Combi Oven IoT Gateway', category: 'IoT Kitchen Devices', manufacturer: 'Rational', model: 'ConnectedCooking', status: 'Approved', processCapabilities: 'Remote Monitoring, Diagnostics' },
  { name: 'Smart Plug (Equipment Monitoring)', category: 'IoT Kitchen Devices', manufacturer: 'Meazon', model: 'SP-100', status: 'Approved', processCapabilities: 'Energy Monitoring, Remote Shutdown' },
  { name: 'Wireless Probe Thermometer', category: 'IoT Kitchen Devices', manufacturer: 'Checkit', model: 'Wireless Probe', status: 'Approved', processCapabilities: 'HACCP Temperature Logging' },
  { name: 'CCTV Camera (Fixed Dome)', category: 'CCTV & Security', manufacturer: 'Hikvision', model: 'DS-2CD2143G2', status: 'Approved', processCapabilities: 'Video Surveillance' },
  { name: 'CCTV Camera (PTZ)', category: 'CCTV & Security', manufacturer: 'Axis Communications', model: 'Q6100-E', status: 'Approved', processCapabilities: 'Video Surveillance, Pan-Tilt-Zoom' },
  { name: 'CCTV Network Video Recorder', category: 'CCTV & Security', manufacturer: 'Hikvision', model: 'DS-7616NI-K2', status: 'Approved', processCapabilities: 'Video Storage, Remote Playback' },
];

const CATEGORY_COUNTS = MOCK_CATALOGUE.reduce<Record<string, number>>((acc, item) => {
  acc[item.category] = (acc[item.category] ?? 0) + 1;
  return acc;
}, {});

const statusColour = (status: string) => (status === 'Deprecated' ? 'default' : 'success');

const CATEGORY_TILE_STYLE = [
  { icon: <Kitchen />, color: '#2e7d32' },
  { icon: <AcUnit />, color: '#0288d1' },
  { icon: <LocalCafe />, color: '#ed6c02' },
];

const EquipmentCatalogue = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const [catalogueListPage, setCatalogueListPage] = useState(0);

  return (
    <PageArea>
      <PageTitle
        subTitle={t('equipmentCatalogueSubtitle', 'Browse the reusable equipment catalogue - the standard makes, models and specifications approved for use across the estate - and drill into an item to view or edit its full specification.')}>
        {t('equipmentCatalogueTitle', 'Equipment')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t('equipmentCatalogueTotalItems', 'Catalogue items')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {MOCK_CATALOGUE.length}
                </Box>
                <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                  {t('equipmentCatalogueTotalItemsSub', 'Standard equipment records')}
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#1976d220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1976d2' }}>
                <SvgIcon fontSize="medium"><Inventory /></SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        {Object.entries(CATEGORY_COUNTS).slice(0, 3).map(([category, count], index) => (
          <Grid item
            key={category}
            xs={12}
            sm={6}
            md={3}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {category}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {count}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {t('equipmentCatalogueCategoryItemsSub', 'Catalogue items in category')}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${CATEGORY_TILE_STYLE[index].color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CATEGORY_TILE_STYLE[index].color }}>
                  <SvgIcon fontSize="medium">{CATEGORY_TILE_STYLE[index].icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('equipmentCatalogueListTitle', 'Standard equipment records')}
            <Pill text={`${MOCK_CATALOGUE.length} ${t('equipmentCatalogueItemsLabel', 'items')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('equipmentCatalogueName', 'Equipment Name')}</TableCell>
                  <TableCell>{t('estateManagementCategory', 'Category')}</TableCell>
                  <TableCell>{t('equipmentManagementType', 'Manufacturer')}</TableCell>
                  <TableCell>{t('equipmentCatalogueModel', 'Model')}</TableCell>
                  <TableCell>{t('equipmentCatalogueProcessCapabilities', 'Process Capabilities')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_CATALOGUE.map((row) => (
                  <TableRow key={row.name}
                    hover
                    onClick={() => navigate(`/estate-management/equipment/${encodeURIComponent(row.name)}`)}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.manufacturer}</TableCell>
                    <TableCell>{row.model}</TableCell>
                    <TableCell>{row.processCapabilities}</TableCell>
                    <TableCell>
                      <Chip label={row.status}
                        size="small"
                        color={statusColour(row.status)}
                        variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={catalogueListPage}
            setPage={setCatalogueListPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>
    </PageArea>
  );
};

export default EquipmentCatalogue;
